/**
 * Act II monsters and guardians (Solenne and its four zones, levels 30–50).
 * Kept apart from enemies.ts to keep that file manageable; only type imports go back
 * the other way, so there's no import cycle.
 */
import type { EnemyDef, ProjectileSpec } from './enemies';

const P = {
  sand: { sprite: 'proj_sand', speed: 130, radius: 4, dmg: 1 },
  bubble: {
    sprite: 'proj_bubble',
    speed: 90,
    radius: 5,
    dmg: 1,
    homing: 1.2,
    life: 4,
    status: { kind: 'slow', duration: 1.5, power: 0.35, chance: 0.5 },
  },
  lightning: { sprite: 'proj_lightning', speed: 180, radius: 4, dmg: 1.1 },
  sunbolt: { sprite: 'proj_sunbolt', speed: 120, radius: 5, dmg: 1.2 },
  needle: { sprite: 'proj_seed', speed: 150, radius: 3, dmg: 0.8 },
  knife: { sprite: 'proj_arrow', speed: 190, radius: 3, dmg: 0.9 },
} satisfies Record<string, ProjectileSpec>;

export const ACT2_ENEMIES: EnemyDef[] = [
  // ----------------------------------------------------- Sunscar Dunes ----
  {
    id: 'scorpion',
    name: 'Dune Scorpion',
    sprite: 'scorpion',
    hp: 170,
    atk: 30,
    def: 22,
    speed: 70,
    radius: 8,
    mass: 0.5,
    xp: 40,
    gold: 18,
    behavior: 'melee',
    aggro: 140,
    element: 'earth',
    attacks: [
      {
        type: 'melee',
        arc: 1.8,
        reach: 24,
        windup: 0.35,
        recover: 0.45,
        cooldown: 1.3,
        range: 26,
        dmg: 1.1,
        status: { kind: 'poison', duration: 3, power: 0.12, chance: 0.4 },
        sfx: 'swing_light',
      },
      {
        type: 'lunge',
        speed: 260,
        duration: 0.25,
        windup: 0.45,
        recover: 0.5,
        cooldown: 3,
        range: 90,
        minRange: 30,
        dmg: 1.3,
      },
    ],
    drops: [{ material: 'pelt', chance: 0.3 }],
    lore: 'Its sting burns like the noon sun that no longer rises.',
  },
  {
    id: 'sand_wraith',
    name: 'Sand Wraith',
    sprite: 'sand_wraith',
    hp: 130,
    atk: 32,
    def: 14,
    speed: 46,
    radius: 7,
    mass: 0.2,
    xp: 42,
    gold: 20,
    behavior: 'caster',
    keepAway: 120,
    flying: true,
    aggro: 170,
    element: 'earth',
    attacks: [
      {
        type: 'shoot',
        proj: P.sand,
        count: 3,
        spread: 0.6,
        windup: 0.6,
        recover: 0.4,
        cooldown: 2.6,
        range: 200,
        dmg: 1,
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 22,
        count: 3,
        spread: 40,
        delay: 1,
        persist: 3,
        windup: 0.6,
        recover: 0.5,
        cooldown: 5,
        range: 200,
        dmg: 0.5,
        status: { kind: 'slow', duration: 1, power: 0.4 },
      },
      {
        type: 'teleport',
        near: 'random',
        windup: 0.4,
        recover: 0.3,
        cooldown: 5,
        range: 70,
        dmg: 0,
        weight: 0.6,
      },
    ],
    drops: [{ material: 'crystal', chance: 0.25 }],
    lore: 'The desert remembers every traveler it swallowed. Sometimes it speaks with their voices.',
  },
  {
    id: 'dune_raider',
    name: 'Dune Raider',
    sprite: 'dune_raider',
    hp: 200,
    atk: 32,
    def: 20,
    speed: 58,
    radius: 7,
    mass: 0.5,
    xp: 44,
    gold: 26,
    behavior: 'melee',
    guard: 0.3,
    aggro: 150,
    attacks: [
      {
        type: 'melee',
        arc: 2.2,
        reach: 26,
        windup: 0.4,
        recover: 0.45,
        cooldown: 1.5,
        range: 28,
        dmg: 1.1,
        repeat: 2,
        sfx: 'swing',
      },
      {
        type: 'shoot',
        proj: P.knife,
        count: 3,
        spread: 0.35,
        windup: 0.5,
        recover: 0.4,
        cooldown: 3.5,
        range: 170,
        minRange: 50,
        dmg: 0.9,
        sfx: 'arrow_shoot',
      },
    ],
    drops: [{ material: 'pelt', chance: 0.3 }],
    lore: 'Since the sun went out, the caravans stopped paying tolls. The raiders stopped asking.',
  },
  {
    id: 'cactoid',
    name: 'Cactoid',
    sprite: 'cactoid',
    hp: 240,
    atk: 28,
    def: 26,
    speed: 0,
    radius: 8,
    mass: 1,
    xp: 38,
    gold: 14,
    behavior: 'turret',
    aggro: 150,
    element: 'nature',
    attacks: [
      {
        type: 'ring',
        proj: P.needle,
        count: 12,
        waves: 2,
        waveDelay: 0.4,
        rotate: 0.26,
        windup: 0.7,
        recover: 0.6,
        cooldown: 3.5,
        range: 170,
        dmg: 0.9,
      },
      {
        type: 'shoot',
        proj: P.needle,
        count: 5,
        spread: 0.5,
        windup: 0.5,
        recover: 0.5,
        cooldown: 2.5,
        range: 170,
        dmg: 0.9,
      },
    ],
    drops: [{ material: 'herb', chance: 0.4 }],
    lore: 'Rooted, patient and absolutely furious.',
  },
  // ------------------------------------------------------ Sunken Temple ----
  {
    id: 'drowned',
    name: 'Drowned Sailor',
    sprite: 'drowned',
    hp: 220,
    atk: 34,
    def: 22,
    speed: 40,
    radius: 7,
    mass: 0.6,
    xp: 46,
    gold: 22,
    behavior: 'melee',
    aggro: 140,
    attacks: [
      {
        type: 'melee',
        arc: 2,
        reach: 24,
        windup: 0.5,
        recover: 0.5,
        cooldown: 1.6,
        range: 26,
        dmg: 1.2,
        repeat: 1,
        status: { kind: 'slow', duration: 1.2, power: 0.3, chance: 0.5 },
        sfx: 'swing_heavy',
      },
      { type: 'nova', radius: 34, windup: 0.8, recover: 0.6, cooldown: 5, range: 30, dmg: 1.2 },
    ],
    drops: [{ material: 'crystal', chance: 0.25 }],
    lore: 'The temple flooded with its sailors still inside. They are still trying to reach the surface.',
  },
  {
    id: 'reef_crab',
    name: 'Reef Crab',
    sprite: 'reef_crab',
    hp: 280,
    atk: 34,
    def: 36,
    speed: 40,
    radius: 10,
    mass: 0.9,
    xp: 48,
    gold: 20,
    behavior: 'melee',
    guard: 0.6,
    aggro: 130,
    attacks: [
      {
        type: 'charge',
        speed: 220,
        length: 140,
        windup: 0.7,
        recover: 0.8,
        cooldown: 4,
        range: 150,
        minRange: 40,
        dmg: 1.4,
      },
      {
        type: 'melee',
        arc: 1.6,
        reach: 26,
        windup: 0.45,
        recover: 0.5,
        cooldown: 1.6,
        range: 28,
        dmg: 1.3,
        sfx: 'swing_heavy',
      },
    ],
    drops: [{ material: 'crystal', chance: 0.3 }],
    lore: 'Its shell is harder than the temple walls. Hit it from behind.',
  },
  {
    id: 'naga_adept',
    name: 'Naga Adept',
    sprite: 'naga_adept',
    hp: 150,
    atk: 36,
    def: 16,
    speed: 50,
    radius: 7,
    mass: 0.3,
    xp: 50,
    gold: 24,
    behavior: 'caster',
    keepAway: 110,
    aggro: 170,
    element: 'ice',
    attacks: [
      {
        type: 'shoot',
        proj: P.bubble,
        count: 2,
        spread: 0.7,
        windup: 0.6,
        recover: 0.4,
        cooldown: 3,
        range: 210,
        dmg: 1,
      },
      {
        type: 'ring',
        proj: { ...P.bubble, homing: 0, speed: 80 },
        count: 10,
        windup: 0.8,
        recover: 0.6,
        cooldown: 6,
        range: 150,
        dmg: 0.9,
      },
      {
        type: 'teleport',
        near: 'random',
        windup: 0.4,
        recover: 0.3,
        cooldown: 5,
        range: 70,
        dmg: 0,
        weight: 0.6,
      },
    ],
    drops: [{ material: 'frost', chance: 0.25 }],
    lore: 'Nereth’s acolytes. They sing to the drowned, and the drowned listen.',
  },
  // ---------------------------------------------------------- Stormspire ----
  {
    id: 'harpy',
    name: 'Storm Harpy',
    sprite: 'harpy',
    hp: 160,
    atk: 36,
    def: 18,
    speed: 72,
    radius: 8,
    mass: 0.3,
    xp: 52,
    gold: 24,
    behavior: 'flyer',
    flying: true,
    aggro: 170,
    attacks: [
      {
        type: 'lunge',
        speed: 250,
        duration: 0.35,
        windup: 0.5,
        recover: 0.6,
        cooldown: 2.2,
        range: 100,
        dmg: 1.3,
      },
      {
        type: 'shoot',
        proj: { ...P.knife, sprite: 'proj_arrow', speed: 170 },
        count: 3,
        spread: 0.5,
        windup: 0.5,
        recover: 0.4,
        cooldown: 3,
        range: 180,
        minRange: 40,
        dmg: 0.9,
      },
    ],
    drops: [{ material: 'pelt', chance: 0.3 }],
    lore: 'They ride the lightning down from the Stormspire and shriek at anything that walks.',
  },
  {
    id: 'storm_elemental',
    name: 'Storm Elemental',
    sprite: 'storm_elemental',
    hp: 150,
    atk: 38,
    def: 16,
    speed: 50,
    radius: 8,
    mass: 0.2,
    xp: 54,
    gold: 22,
    behavior: 'caster',
    keepAway: 120,
    flying: true,
    aggro: 180,
    attacks: [
      {
        type: 'shoot',
        proj: P.lightning,
        count: 1,
        spread: 0,
        bursts: 3,
        burstDelay: 0.25,
        windup: 0.6,
        recover: 0.4,
        cooldown: 2.8,
        range: 210,
        dmg: 1,
        sfx: 'lightning',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 18,
        count: 3,
        spread: 50,
        delay: 0.9,
        stagger: 0.2,
        windup: 0.5,
        recover: 0.5,
        cooldown: 5,
        range: 210,
        dmg: 1.3,
        sfx: 'lightning',
      },
      {
        type: 'nova',
        radius: 36,
        windup: 0.6,
        recover: 0.5,
        cooldown: 4,
        range: 32,
        dmg: 1.2,
        sfx: 'lightning',
      },
    ],
    drops: [{ material: 'crystal', chance: 0.3 }],
    lore: 'A thunderstorm that learned to be angry on purpose.',
  },
  {
    id: 'stone_sentinel',
    name: 'Stone Sentinel',
    sprite: 'stone_sentinel',
    hp: 320,
    atk: 38,
    def: 40,
    speed: 30,
    radius: 10,
    mass: 0.95,
    xp: 58,
    gold: 26,
    behavior: 'melee',
    aggro: 130,
    element: 'earth',
    immune: ['freeze'],
    attacks: [
      {
        type: 'melee',
        arc: 2.6,
        reach: 30,
        windup: 0.7,
        recover: 0.7,
        cooldown: 2,
        range: 32,
        dmg: 1.6,
        sfx: 'swing_heavy',
      },
      { type: 'nova', radius: 44, windup: 0.9, recover: 0.8, cooldown: 5, range: 40, dmg: 1.5, sfx: 'slam' },
      {
        type: 'charge',
        speed: 200,
        length: 120,
        windup: 0.8,
        recover: 0.9,
        cooldown: 6,
        range: 140,
        minRange: 50,
        dmg: 1.5,
      },
    ],
    drops: [{ material: 'crystal', chance: 0.3 }],
    lore: 'The Order carved them to guard the road to the Sanctum. They still do.',
  },
  // ---------------------------------------------------- Eclipse Sanctum ----
  {
    id: 'eclipse_knight',
    name: 'Eclipse Knight',
    sprite: 'eclipse_knight',
    hp: 260,
    atk: 42,
    def: 34,
    speed: 55,
    radius: 8,
    mass: 0.7,
    xp: 64,
    gold: 30,
    behavior: 'melee',
    guard: 0.5,
    aggro: 150,
    element: 'void',
    attacks: [
      {
        type: 'melee',
        arc: 2.4,
        reach: 28,
        windup: 0.45,
        recover: 0.5,
        cooldown: 1.6,
        range: 30,
        dmg: 1.2,
        repeat: 2,
        sfx: 'swing_heavy',
      },
      {
        type: 'lunge',
        speed: 260,
        duration: 0.3,
        windup: 0.55,
        recover: 0.6,
        cooldown: 3,
        range: 110,
        minRange: 40,
        dmg: 1.4,
      },
      {
        type: 'shoot',
        proj: P.sunbolt,
        count: 3,
        spread: 0.4,
        windup: 0.6,
        recover: 0.5,
        cooldown: 4,
        range: 180,
        minRange: 60,
        dmg: 1,
      },
    ],
    drops: [{ material: 'void', chance: 0.3 }],
    lore: 'Knights of the Order who swore their blades to Aurelian’s endless dusk.',
  },
  {
    id: 'dusk_wisp',
    name: 'Dusk Wisp',
    sprite: 'dusk_wisp',
    hp: 70,
    atk: 40,
    def: 10,
    speed: 85,
    radius: 5,
    mass: 0.1,
    xp: 24,
    gold: 8,
    behavior: 'kamikaze',
    flying: true,
    aggro: 170,
    element: 'void',
    attacks: [
      {
        type: 'nova',
        radius: 40,
        windup: 0.6,
        recover: 0,
        cooldown: 1,
        range: 26,
        dmg: 1.6,
        selfDestruct: true,
        sfx: 'void_pulse',
      },
    ],
    drops: [{ material: 'void', chance: 0.2 }],
    lore: 'Scraps of the last sunset, burning out.',
  },
  // ------------------------------------------------------------ guardians ----
  {
    id: 'sandmaw',
    name: 'Sandmaw',
    sprite: 'boss_sandmaw',
    hp: 2600,
    atk: 30,
    def: 26,
    speed: 0,
    radius: 22,
    mass: 1,
    xp: 700,
    gold: 2000,
    behavior: 'turret',
    aggro: 999,
    element: 'earth',
    immune: ['freeze'],
    boss: {
      title: 'The Dune That Hungers',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.66,
          speedMult: 1,
          cooldownMult: 0.8,
          shout: 'The dunes shake!',
          summon: { enemy: 'scorpion', count: 3 },
        },
        { at: 0.33, speedMult: 1, cooldownMult: 0.6, shout: 'SSSKRRAAAAH!' },
      ],
    },
    attacks: [
      {
        type: 'line',
        length: 190,
        width: 18,
        windup: 0.8,
        recover: 0.6,
        cooldown: 3.5,
        range: 400,
        dmg: 1.6,
        sfx: 'roar',
        weight: 1.2,
      },
      {
        type: 'ring',
        proj: { ...P.sand, speed: 100, scale: 1.4 },
        count: 16,
        waves: 2,
        waveDelay: 0.5,
        rotate: 0.2,
        windup: 0.8,
        recover: 0.8,
        cooldown: 5,
        range: 400,
        dmg: 1,
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 24,
        count: 4,
        spread: 70,
        delay: 1,
        stagger: 0.15,
        windup: 0.6,
        recover: 0.7,
        cooldown: 6,
        range: 400,
        dmg: 1.3,
        shout: 'Sand erupts!',
      },
      {
        type: 'teleport',
        near: 'player',
        novaRadius: 50,
        windup: 1.1,
        recover: 0.9,
        cooldown: 7,
        range: 400,
        dmg: 1.8,
        shout: 'Sandmaw burrows!',
      },
      {
        type: 'summon',
        enemy: 'scorpion',
        count: 2,
        max: 4,
        windup: 0.9,
        recover: 0.8,
        cooldown: 12,
        range: 400,
        dmg: 0,
        sfx: 'summon',
        weight: 0.6,
        phase: 1,
      },
    ],
    lore: 'It swallowed the Order’s last caravan whole — and the Grandmaster’s journal with it.',
  },
  {
    id: 'nereth',
    name: 'Nereth',
    sprite: 'boss_nereth',
    hp: 2900,
    atk: 34,
    def: 26,
    speed: 55,
    radius: 12,
    mass: 1,
    xp: 850,
    gold: 2400,
    behavior: 'boss',
    aggro: 999,
    element: 'ice',
    immune: ['slow'],
    boss: {
      title: 'The Tide Oracle',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.5,
          speedMult: 1.2,
          cooldownMult: 0.7,
          shout: 'I have SEEN the endless dusk!',
          summon: { enemy: 'drowned', count: 3 },
        },
      ],
    },
    attacks: [
      {
        type: 'shoot',
        proj: { ...P.bubble, homing: 0.8 },
        count: 4,
        spread: 0.9,
        bursts: 2,
        burstDelay: 0.35,
        windup: 0.6,
        recover: 0.5,
        cooldown: 3,
        range: 400,
        dmg: 1,
      },
      {
        type: 'beam',
        length: 170,
        width: 12,
        duration: 1.6,
        sweep: 1.4,
        windup: 0.9,
        recover: 0.9,
        cooldown: 6,
        range: 180,
        dmg: 0.5,
        shout: 'Drown!',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 28,
        count: 3,
        spread: 70,
        delay: 1.1,
        persist: 4,
        windup: 0.6,
        recover: 0.6,
        cooldown: 6,
        range: 400,
        dmg: 0.5,
        status: { kind: 'slow', duration: 1, power: 0.5 },
      },
      {
        type: 'teleport',
        near: 'player',
        novaRadius: 48,
        windup: 0.9,
        recover: 0.8,
        cooldown: 7,
        range: 400,
        dmg: 1.5,
      },
      {
        type: 'ring',
        proj: { ...P.bubble, homing: 0, speed: 95 },
        count: 20,
        waves: 3,
        waveDelay: 0.45,
        rotate: 0.16,
        windup: 0.8,
        recover: 0.8,
        cooldown: 7,
        range: 400,
        dmg: 0.9,
        phase: 1,
      },
    ],
    lore: 'The Order’s oracle saw the eclipse coming years ago. When she tried to warn them, the Dusk Shard drowned her voice — and then the rest of her.',
  },
  {
    id: 'voltaris',
    name: 'Voltaris',
    sprite: 'boss_voltaris',
    hp: 3200,
    atk: 38,
    def: 28,
    speed: 70,
    radius: 16,
    mass: 1,
    xp: 1000,
    gold: 2800,
    behavior: 'boss',
    flying: true,
    aggro: 999,
    immune: ['freeze'],
    boss: {
      title: 'The Storm Roc',
      music: 'boss',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.5,
          speedMult: 1.25,
          cooldownMult: 0.7,
          shout: 'KRRAAAAAW!',
          summon: { enemy: 'harpy', count: 3 },
        },
      ],
    },
    attacks: [
      {
        type: 'dive',
        radius: 60,
        airTime: 1.8,
        windup: 0.6,
        recover: 1,
        cooldown: 7,
        range: 400,
        dmg: 2,
        sfx: 'roar',
        shout: 'Voltaris dives!',
      },
      {
        type: 'shoot',
        proj: { ...P.lightning, speed: 200 },
        count: 3,
        spread: 0.5,
        bursts: 3,
        burstDelay: 0.25,
        windup: 0.6,
        recover: 0.6,
        cooldown: 3,
        range: 400,
        dmg: 1,
        sfx: 'lightning',
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 22,
        count: 6,
        spread: 90,
        delay: 1,
        stagger: 0.12,
        windup: 0.6,
        recover: 0.7,
        cooldown: 6,
        range: 400,
        dmg: 1.3,
        sfx: 'lightning',
        shout: 'The sky splits!',
      },
      {
        type: 'cone',
        length: 140,
        arc: 0.9,
        duration: 1.2,
        sweep: 0.8,
        windup: 0.8,
        recover: 0.8,
        cooldown: 6,
        range: 150,
        dmg: 0.5,
        status: { kind: 'slow', duration: 1.5, power: 0.4 },
      },
      {
        type: 'spiral',
        proj: { ...P.lightning, speed: 110 },
        arms: 3,
        shots: 16,
        interval: 0.12,
        turn: 0.28,
        windup: 0.8,
        recover: 1,
        cooldown: 8,
        range: 400,
        dmg: 0.9,
        phase: 1,
      },
    ],
    lore: 'The Stormspire’s lightning is its heartbeat. The Grandmaster chained the key to the Sanctum inside it.',
  },
  {
    id: 'aurelian',
    name: 'Aurelian',
    sprite: 'boss_aurelian',
    scale: 1.15,
    hp: 4200,
    atk: 44,
    def: 34,
    speed: 70,
    radius: 11,
    mass: 1,
    xp: 2000,
    gold: 5000,
    behavior: 'boss',
    aggro: 999,
    element: 'void',
    immune: ['freeze'],
    boss: {
      title: 'Grandmaster of the Endless Dusk',
      music: 'final_boss2',
      portrait: 'aurelian',
      shardName: 'Dusk Shard',
      phases: [
        { at: 1, speedMult: 1, cooldownMult: 1 },
        {
          at: 0.66,
          speedMult: 1.15,
          cooldownMult: 0.8,
          shout: 'You would choose falling skies over peace?',
          summon: { enemy: 'eclipse_knight', count: 2 },
        },
        { at: 0.33, speedMult: 1.3, cooldownMult: 0.6, shout: 'Then let the DUSK be ETERNAL!' },
      ],
    },
    attacks: [
      {
        type: 'melee',
        arc: 3,
        reach: 40,
        windup: 0.55,
        recover: 0.55,
        cooldown: 2,
        range: 42,
        dmg: 1.4,
        repeat: 2,
        sfx: 'swing_heavy',
      },
      {
        type: 'lunge',
        speed: 320,
        duration: 0.3,
        windup: 0.6,
        recover: 0.6,
        cooldown: 3.5,
        range: 150,
        minRange: 50,
        dmg: 1.5,
      },
      {
        type: 'beam',
        length: 200,
        width: 14,
        duration: 1.8,
        sweep: 1.8,
        windup: 1,
        recover: 1,
        cooldown: 7,
        range: 210,
        dmg: 0.6,
        shout: 'Behold the black sun!',
      },
      {
        type: 'ring',
        proj: { ...P.sunbolt, speed: 100 },
        count: 18,
        waves: 3,
        waveDelay: 0.4,
        rotate: 0.17,
        windup: 0.8,
        recover: 0.8,
        cooldown: 6,
        range: 400,
        dmg: 1,
      },
      {
        type: 'aoe',
        at: 'player',
        radius: 26,
        count: 5,
        spread: 80,
        delay: 1,
        stagger: 0.15,
        windup: 0.6,
        recover: 0.7,
        cooldown: 7,
        range: 400,
        dmg: 1.4,
        phase: 1,
      },
      {
        type: 'teleport',
        near: 'player',
        novaRadius: 52,
        windup: 0.8,
        recover: 0.8,
        cooldown: 6,
        range: 400,
        dmg: 1.6,
      },
      {
        type: 'spiral',
        proj: { ...P.sunbolt, speed: 105 },
        arms: 5,
        shots: 18,
        interval: 0.1,
        turn: 0.24,
        windup: 0.8,
        recover: 1,
        cooldown: 8,
        range: 400,
        dmg: 0.9,
        phase: 2,
      },
    ],
    lore: 'He watched the sky fall once and decided it would never happen again — even if the sun had to go out forever.',
  },
];
